import { LOCALES, type Locale } from '@infiltre/shared';
import { COUNTRY_BY_LOCALE, FLAG_BY_LOCALE, NATIVE_NAME_BY_LOCALE } from './icons/flags';

export type LanguageRowListProps = {
  value: Locale;
  onSelect: (l: Locale) => void;
  className?: string;
};

/**
 * Vertical list of language rows. Each row renders the flag, the
 * endonym (native language name) over the endonym country label, and
 * a red dot when selected. Borders mirror the host-settings `Row`
 * recipe but the active state uses brand-red instead of ink.
 */
export const LanguageRowList = ({ value, onSelect, className }: LanguageRowListProps) => (
  <ul className={['flex flex-col gap-2', className ?? ''].filter(Boolean).join(' ')}>
    {LOCALES.map((code) => {
      const Flag = FLAG_BY_LOCALE[code];
      const selected = code === value;
      return (
        <li key={code}>
          <button
            type="button"
            onClick={() => onSelect(code)}
            aria-pressed={selected}
            aria-label={NATIVE_NAME_BY_LOCALE[code]}
            className={[
              'flex w-full items-center gap-3 rounded-lg border-[1.5px] px-3 py-2.5 text-left transition-colors duration-150',
              selected
                ? 'border-[var(--color-stamp-red)] bg-[color-mix(in_oklch,var(--color-stamp-red)_8%,var(--color-cream))]'
                : 'border-ink/20 bg-cream hover:border-ink/40',
            ].join(' ')}
          >
            <Flag size={60} />
            <div className="flex flex-1 flex-col leading-tight">
              <span className="font-display font-extrabold text-[18px] text-ink">
                {NATIVE_NAME_BY_LOCALE[code]}
              </span>
              <span className="text-[13px] text-ink/60">{COUNTRY_BY_LOCALE[code]}</span>
            </div>
            {selected && (
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--color-stamp-red)]"
              />
            )}
          </button>
        </li>
      );
    })}
  </ul>
);
